import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useAuthStore } from '../store/authStore';

interface FormErrors {
  email?: string;
  password?: string;
  displayName?: string;
  general?: string;
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const setAuth = useAuthStore((state) => state.setAuth);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);

  // Get the redirect destination from location state (set by ProtectedRoute or direct navigation)
  // Preserve query params if they exist in the redirect path
  const from = (location.state as { from?: string })?.from || '/dashboard';

  /**
   * Validate email format
   */
  const validateEmail = (email: string): string | undefined => {
    if (!email) {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Invalid email format';
    }
    return undefined;
  };

  /**
   * Validate password strength
   */
  const validatePassword = (password: string): string | undefined => {
    if (!password) {
      return 'Password is required';
    }
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number';
    }
    return undefined;
  };

  /**
   * Validate display name
   */
  const validateDisplayName = (displayName: string): string | undefined => {
    if (!displayName) {
      return 'Display name is required';
    }
    if (displayName.trim().length === 0) {
      return 'Display name cannot be empty';
    }
    if (displayName.trim().length < 2) {
      return 'Display name must be at least 2 characters';
    }
    return undefined;
  };

  /**
   * Validate form fields
   */
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    const emailError = validateEmail(email);
    if (emailError) {
      newErrors.email = emailError;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      newErrors.password = passwordError;
    }

    if (isRegistering) {
      const displayNameError = validateDisplayName(displayName);
      if (displayNameError) {
        newErrors.displayName = displayNameError;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    setErrors({});

    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      if (isRegistering) {
        // Register new user
        await apiClient.post('/api/auth/register', {
          email: email.trim(),
          password,
          displayName: displayName.trim(),
        });

        // After successful registration, automatically log in
        const loginResponse = await apiClient.post<{
          token: string;
          userId: string;
          email: string;
          displayName: string;
        }>('/api/auth/login', {
          email: email.trim(),
          password,
        });

        // Store auth state in Zustand store
        setAuth(loginResponse.token, {
          userId: loginResponse.userId,
          email: loginResponse.email,
          displayName: loginResponse.displayName,
        });

        // Navigate to intended destination or dashboard
        navigate(from, { replace: true });
      } else {
        // Login existing user
        const response = await apiClient.post<{
          token: string;
          userId: string;
          email: string;
          displayName: string;
        }>('/api/auth/login', {
          email: email.trim(),
          password,
        });

        // Store auth state in Zustand store
        setAuth(response.token, {
          userId: response.userId,
          email: response.email,
          displayName: response.displayName,
        });

        // Navigate to intended destination or dashboard
        navigate(from, { replace: true });
      }
    } catch (error) {
      console.error('Authentication error:', error);

      // Display error message
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      setErrors({ general: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Toggle between login and registration
   */
  const toggleMode = () => {
    setIsRegistering(!isRegistering);
    setErrors({});
    setDisplayName('');
  };

  return (
    <div className="login-container">
      <h1>Silent Disco - Host</h1>
      <h2>{isRegistering ? 'Register' : 'Login'}</h2>

      <form onSubmit={handleSubmit} noValidate>
        {errors.general && (
          <div className="error-message general-error" role="alert">
            {errors.general}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (errors.email) {
                setErrors({ ...errors, email: undefined });
              }
            }}
            onBlur={() => {
              const error = validateEmail(email);
              if (error) {
                setErrors({ ...errors, email: error });
              }
            }}
            disabled={isLoading}
            placeholder="your@email.com"
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? 'email-error' : undefined}
          />
          {errors.email && (
            <span id="email-error" className="error-message" role="alert">
              {errors.email}
            </span>
          )}
        </div>

        {isRegistering && (
          <div className="form-group">
            <label htmlFor="displayName">Display Name</label>
            <input
              id="displayName"
              type="text"
              value={displayName}
              onChange={(e) => {
                setDisplayName(e.target.value);
                if (errors.displayName) {
                  setErrors({ ...errors, displayName: undefined });
                }
              }}
              onBlur={() => {
                const error = validateDisplayName(displayName);
                if (error) {
                  setErrors({ ...errors, displayName: error });
                }
              }}
              disabled={isLoading}
              placeholder="Your Name"
              aria-invalid={!!errors.displayName}
              aria-describedby={errors.displayName ? 'displayName-error' : undefined}
            />
            {errors.displayName && (
              <span id="displayName-error" className="error-message" role="alert">
                {errors.displayName}
              </span>
            )}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (errors.password) {
                setErrors({ ...errors, password: undefined });
              }
            }}
            onBlur={() => {
              if (isRegistering) {
                const error = validatePassword(password);
                if (error) {
                  setErrors({ ...errors, password: error });
                }
              }
            }}
            disabled={isLoading}
            placeholder="••••••••"
            aria-invalid={!!errors.password}
            aria-describedby={errors.password ? 'password-error' : undefined}
          />
          {errors.password && (
            <span id="password-error" className="error-message" role="alert">
              {errors.password}
            </span>
          )}
          {isRegistering && !errors.password && (
            <span className="help-text">
              Must be at least 8 characters with uppercase, lowercase, and number
            </span>
          )}
        </div>

        <button type="submit" disabled={isLoading}>
          {isLoading ? 'Please wait...' : (isRegistering ? 'Register' : 'Login')}
        </button>
      </form>

      <p>
        {isRegistering ? 'Already have an account?' : "Don't have an account?"}
        {' '}
        <button
          type="button"
          onClick={toggleMode}
          className="link-button"
          disabled={isLoading}
        >
          {isRegistering ? 'Login' : 'Register'}
        </button>
      </p>
    </div>
  );
}
